import { hasSysId, isMediaAssetLike, isTypenameObject } from './type-guards';

export function removeUnresolvedCollectionItems(entry: Record<string, unknown>): void {
  for (const [, value] of Object.entries(entry)) {
    if (!value || typeof value !== 'object') continue;

    if (Array.isArray(value)) {
      for (const item of value) {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          removeUnresolvedCollectionItems(item as Record<string, unknown>);
        }
      }
      continue;
    }

    const obj = value as Record<string, unknown>;
    const items = obj.items;
    if (Array.isArray(items)) {
      const filtered = items.filter(item => !(hasSysId(item) && !isTypenameObject(item) && !isMediaAssetLike(item)));
      obj.items = filtered;
      for (const item of filtered) {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          removeUnresolvedCollectionItems(item as Record<string, unknown>);
        }
      }
      continue;
    }

    removeUnresolvedCollectionItems(obj);
  }
}

/**
 * Flattens page modules into modulesCollection.items.
 */
export function flattenModules(pageData: Record<string, unknown>): Array<Record<string, unknown>> {
  const flattened: Array<Record<string, unknown>> = [];

  // headerBannerCollection
  const headerBanner = pageData.headerBannerCollection as { items?: Array<Record<string, unknown>> } | undefined;
  if (headerBanner?.items?.length) {
    flattened.push(...headerBanner.items);
  }

  // nav
  if (pageData.nav) {
    flattened.push(pageData.nav as Record<string, unknown>);
  }

  // hero (exclude for BlogPage)
  const shouldIncludeHero = pageData.__typename !== 'BlogPage';
  if (pageData.hero && shouldIncludeHero) {
    flattened.push(pageData.hero as Record<string, unknown>);
  }

  // modules
  const modules = pageData.modules as Array<Record<string, unknown>> | undefined;
  if (modules?.length) {
    flattened.push(...modules);
  }

  // footer
  if (pageData.footer) {
    flattened.push(pageData.footer as Record<string, unknown>);
  }

  // disclosuresCollection
  const disclosures = pageData.disclosuresCollection as { items?: Array<Record<string, unknown>> } | undefined;
  if (disclosures?.items?.length) {
    flattened.push(...disclosures.items);
  }

  // disclosuresBottomCollection
  const disclosuresBottom = pageData.disclosuresBottomCollection as
    | { items?: Array<Record<string, unknown>> }
    | undefined;
  if (disclosuresBottom?.items?.length) {
    flattened.push(...disclosuresBottom.items);
  }

  return flattened;
}

/**
 * Checks if an entry has Ninetailed experiments.
 * Experiments are indicated by the presence of nt_experiences field.
 * After adaptation, this may be nt_experiencesCollection.items.
 */
function hasNinetailedExperiments(module: Record<string, unknown>): boolean {
  // After adaptation, nt_experiences may be either:
  // - an array of entries/links
  // - a collection-like object: { items: [...] }
  const raw = module.nt_experiences;
  if (Array.isArray(raw)) {
    return raw.length > 0;
  }

  if (raw && typeof raw === 'object') {
    const maybeCollection = raw as Record<string, unknown>;
    const items = maybeCollection.items;
    if (Array.isArray(items)) {
      return items.length > 0;
    }
  }

  // Backward-compat: some code paths may map to nt_experiencesCollection
  const collection = module.nt_experiencesCollection;
  if (collection && typeof collection === 'object') {
    const maybeCollection = collection as Record<string, unknown>;
    const items = maybeCollection.items;
    if (Array.isArray(items)) {
      return items.length > 0;
    }
  }

  // Backward-compat: some code paths may map to ntExperiencesCollection (camelCase)
  const camelCollection = module.ntExperiencesCollection;
  if (camelCollection && typeof camelCollection === 'object') {
    const maybeCollection = camelCollection as Record<string, unknown>;
    const items = maybeCollection.items;
    if (Array.isArray(items)) {
      return items.length > 0;
    }
  }

  return false;
}

/**
 * Annotates a module with hasExperiments flag.
 * Detects Ninetailed experiments via nt_experiences field.
 */
export function annotateModule(module: Record<string, unknown>): Record<string, unknown> {
  if (module.__typename === 'Keyline') {
    return { __typename: 'Keyline' };
  }
  return {
    ...module,
    hasExperiments: hasNinetailedExperiments(module),
  };
}

/**
 * Wraps an array in a collection structure { items: [...] }.
 */
export function wrapCollection(items: Array<Record<string, unknown>>): { items: Array<Record<string, unknown>> } {
  return { items };
}
